import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { HiveMark } from '../components/HiveMark';
import { AppSplash } from '../components/AppSplash';
import { Alert, LinkButton } from '../components/ui';

/**
 * Copy for the fixed set of error codes the API redirects with. The API never
 * reflects raw text into the page, so there is nothing here to inject into.
 */
const ERROR_MESSAGES: Record<string, string> = {
  domain: 'Use your Foundra Studio account. Personal Google accounts cannot sign in to Hive.',
  not_allowed: "That account isn't on the access list yet. Ask an admin to add you.",
  invalid_state: 'That sign-in attempt expired. Please try again.',
  oauth: "Sign-in didn't complete. Please try again.",
};

export function Login() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();

  if (loading) return <AppSplash />;
  if (user) return <Navigate to="/" replace />;

  const errorCode = searchParams.get('error');
  const errorMessage = errorCode ? (ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES.oauth) : null;

  // Forward where the user was headed. The server re-validates that this is a
  // site-relative path before redirecting to it.
  const next = searchParams.get('next');
  const signInHref = next ? `/api/auth/google?next=${encodeURIComponent(next)}` : '/api/auth/google';

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <HiveMark className="mx-auto block h-11 w-11 text-honey-500" />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">Sign in to Hive</h1>
          <p className="mt-1.5 text-sm text-stone-500">Foundra Studio accounts only</p>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          {/* The shared Alert, rather than its class string written out again --
              which is what this was, and so the one error surface in the app
              that would not have picked up any change made to the others. */}
          {errorMessage ? (
            <div className="mb-4">
              <Alert>{errorMessage}</Alert>
            </div>
          ) : null}

          {/*
            A plain anchor, not a router link and not a fetch: the OAuth
            handshake is a top-level navigation to Google and back, which an XHR
            could not follow and the router has no business intercepting.
          */}
          <LinkButton href={signInHref} className="w-full py-2.5">
            <GoogleMark />
            Continue with Google
          </LinkButton>
        </div>

        <p className="mt-6 text-center text-xs text-stone-500">
          Access is managed in Google Workspace.
        </p>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.96 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
